import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from "react";
import Modal from "@modalSrc/Modal";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { FaPlusSquare } from "react-icons/fa";
import { IoTrashOutline } from "react-icons/io5";
import { CiEdit } from "react-icons/ci";
import { RiSave3Fill } from "react-icons/ri";
import { ImCancelCircle } from "react-icons/im";
import { toast } from "react-toastify";
import RightTimeApi from "../../API";
import Helpers from "../../Helpers";
import Enums from "../../enums";
import DateTimePicker from "../DateTimePicker";
import SupervisorSelectModal from "./SupervisorSelectModal";
import '@/style/sass/_modal.scss';
import '@/style/sass/_testInstanceModal.scss';
import { readPermIdsFromSession } from "@/i18n/sessionUser";
import { downloadAuthenticatedFile } from "@/utils/authenticatedFileDownload";
import { apiMessage } from "@/utils/apiMessage";

type InstanceDataPointRow = {
  id: number;
  dataKey: string;
  dataValue: string;
  active: boolean;
  /** ISO date string from API */
  createdTime: string;
};

/** Summary score fields persisted via TestInstance PUT → TestInstanceData score keys. */
const SUMMARY_SCORE_FIELDS = [
  { field: "ApplicantScore", label: "Applicant Score", scoreType: Enums.TEST_SCORE_TYPES.Applicant },
  { field: "CompanyScore", label: "Company Score", scoreType: Enums.TEST_SCORE_TYPES.Company },
  { field: "OverallScore", label: "Overall Score", scoreType: Enums.TEST_SCORE_TYPES.Overall },
] as const;

function getActiveScoreFromList(
  scores: { active?: boolean; scoreType?: number; score?: string | null }[] | undefined,
  scoreType: number,
  instanceDataActiveByKey?: Record<string, string | null | undefined>
): string {
  const keyByType: Record<number, string> = {
    [Enums.TEST_SCORE_TYPES.Applicant]: "ApplicantScore",
    [Enums.TEST_SCORE_TYPES.Company]: "CompanyScore",
    [Enums.TEST_SCORE_TYPES.Overall]: "OverallScore",
  };
  const dataKey = keyByType[scoreType];
  if (dataKey && instanceDataActiveByKey?.[dataKey] != null && String(instanceDataActiveByKey[dataKey]).trim() !== "") {
    return String(instanceDataActiveByKey[dataKey]);
  }
  if (!scores?.length) {
    return "";
  }
  const match = scores.find((s) => s.active !== false && s.scoreType === scoreType);
  return match?.score != null ? String(match.score) : "";
}

function instanceDataRowsEqual(a: InstanceDataPointRow[], b: InstanceDataPointRow[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const mb = new Map(b.map((r) => [r.id, r]));
  for (const row of a) {
    const o = mb.get(row.id);
    if (
      !o ||
      row.dataKey !== o.dataKey ||
      row.dataValue !== o.dataValue ||
      row.active !== o.active
    ) {
      return false;
    }
  }
  return true;
}

/** Vertical padding on `.tabsContentContainer` (top + bottom) — keep in sync with _testInstanceModal.scss */
const TAB_CONTENT_VERTICAL_PADDING_PX = 32;

export default function TestInstanceModal(props) {
  const { isOpen, onClose, testId, testInstanceId, updateSpinner } = props || {};
  
  const [instanceData, setInstanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [originalPayload, setOriginalPayload] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [testInstanceStatuses, setTestInstanceStatuses] = useState([]);
  const [supervisorSelectInstanceId, setSupervisorSelectInstanceId] = useState(null);
  const [companyId, setCompanyId] = useState(props?.companyId || null);
  const [sessionPermIds, setSessionPermIds] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState<"summary" | "data">("summary");
  const [localDataPoints, setLocalDataPoints] = useState<InstanceDataPointRow[]>([]);
  const [editingDataRow, setEditingDataRow] = useState<InstanceDataPointRow | null>(null);
  const summaryPanelRef = useRef<HTMLDivElement>(null);
  /** Total height of `.tabsContentContainer`: Summary content + its vertical padding so Data tab matches. */
  const [tabsContentHeightPx, setTabsContentHeightPx] = useState<number | null>(null);
  /** Like AdminCompaniesTab: false = list active rows only; true = include inactive history. */
  const [showAllInstanceData, setShowAllInstanceData] = useState(true);
  /** Snapshot from last API load; used to diff pending instance-data edits. */
  const [baselineDataPoints, setBaselineDataPoints] = useState<InstanceDataPointRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const tempDataRowIdRef = useRef(0);

  // Extract permissions from sessionStorage
  useEffect(() => {
    setSessionPermIds(readPermIdsFromSession());
  }, []);

  // Check if user can edit test instances (SystemAdmin or TestEdit permission)
  const isSystemAdmin = sessionPermIds.includes(254); // 254 is SystemAdmin permission
  const canEditTestInstance =
    isSystemAdmin || sessionPermIds.includes(Enums.ROLE_PERMS.TestEdit);

  useEffect(() => {
    if (isOpen) {
      setActiveTab("summary");
    }
  }, [isOpen, testInstanceId]);

  useEffect(() => {
    setTabsContentHeightPx(null);
    setShowAllInstanceData(true);
  }, [testInstanceId]);

  useEffect(() => {
    if (!isOpen || !testId || testId === null || testInstanceId === null || testInstanceId === undefined) {
      return;
    }

    loadTestInstance();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, testId, testInstanceId]);

  // Update available statuses when CompanyTestTypeId changes
  useEffect(() => {
    if (payload?.CompanyTestTypeId && instanceData?.availableCompanyTestTypes) {
      const ctt = instanceData.availableCompanyTestTypes.find(t => t.id === payload.CompanyTestTypeId);
      if (ctt && ctt.statuses) {
        setAvailableStatuses(ctt.statuses);
      } else {
        setAvailableStatuses([]);
      }
    } else {
      setAvailableStatuses([]);
    }
  }, [payload?.CompanyTestTypeId, instanceData?.availableCompanyTestTypes]);

  useEffect(() => {
    if (editingDataRow !== null) {
      return;
    }
    const pts = instanceData?.instanceDataPoints;
    if (!Array.isArray(pts)) {
      setLocalDataPoints([]);
      setBaselineDataPoints([]);
      return;
    }
    const mapped = pts.map(
      (r: { id?: number; dataKey?: string; dataValue?: string | null; active?: boolean; createdTime?: string }) => ({
        id: Number(r.id),
        dataKey: String(r.dataKey ?? ""),
        dataValue: r.dataValue != null ? String(r.dataValue) : "",
        active: r.active !== false,
        createdTime: r.createdTime != null ? String(r.createdTime) : "",
      })
    );
    setLocalDataPoints(mapped);
    setBaselineDataPoints(mapped.map((r) => ({ ...r })));
    // Intentionally omit editingDataRow: only resync from API when not editing (see editingDataRow == null dep).
  }, [instanceData?.instanceDataPoints, editingDataRow === null]);

  const instanceDataDirty = useMemo(
    () => !instanceDataRowsEqual(localDataPoints, baselineDataPoints),
    [localDataPoints, baselineDataPoints]
  );

  const scoresDirty = useMemo(() => {
    if (!payload || !originalPayload) {
      return false;
    }
    return (
      (payload.ApplicantScore ?? "") !== (originalPayload.ApplicantScore ?? "") ||
      (payload.CompanyScore ?? "") !== (originalPayload.CompanyScore ?? "") ||
      (payload.OverallScore ?? "") !== (originalPayload.OverallScore ?? "")
    );
  }, [payload, originalPayload]);

  /** Data tab needs a fixed-height shell so only the instance-data table scrolls. Summary uses natural height — never clip or scroll inside the summary panel. */
  const tabsContentContainerStyle = useMemo(() => {
    if (activeTab === "data") {
      if (tabsContentHeightPx != null) {
        return {
          height: tabsContentHeightPx,
          minHeight: tabsContentHeightPx,
          maxHeight: tabsContentHeightPx,
          overflow: "hidden" as const,
        };
      }
      return { minHeight: 400, overflow: "hidden" as const };
    }
    return {
      height: "auto",
      minHeight: 0,
      maxHeight: "none",
      overflow: "visible" as const,
    };
  }, [activeTab, tabsContentHeightPx]);

  useLayoutEffect(() => {
    if (activeTab !== "summary" || !summaryPanelRef.current) {
      return;
    }
    const el = summaryPanelRef.current;
    const updateHeight = () => {
      const rect = el.getBoundingClientRect();
      const h = Math.ceil(Math.max(el.scrollHeight, rect.height));
      if (h > 0) {
        setTabsContentHeightPx(h + TAB_CONTENT_VERTICAL_PADDING_PX);
      }
    };
    updateHeight();
    requestAnimationFrame(() => requestAnimationFrame(updateHeight));
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(updateHeight);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [
    activeTab,
    instanceData,
    payload,
    loading,
    hasChanges,
    instanceDataDirty,
    localDataPoints,
    testInstanceId,
  ]);

  async function loadTestInstance() {
    if (updateSpinner) updateSpinner(true);
    setLoading(true);
    setLoadError(null);
    setInstanceData(null);

    try {
      const resp = await RightTimeApi.getAsync(`/Test/${testId}/Instance/${testInstanceId}`);
      
      if (resp && resp.ok && resp.jsonData) {
        setInstanceData(resp.jsonData);

        const scores = resp.jsonData.testInstance?.scores;
        const activeByKey = resp.jsonData.testInstance?.instanceDataActiveByKey ?? {};
        const initialPayload = {
          Date: testInstanceId === 0 ? new Date().toISOString() : (resp.jsonData.testInstance?.createTime || new Date().toISOString()),
          CompanyTestTypeId: resp.jsonData.testInstance?.companyTestType?.id || null,
          JobId: resp.jsonData.testInstance?.companyJobId || null,
          SiteId: resp.jsonData.testInstance?.siteId || null,
          Notes: resp.jsonData.testInstance?.notes || null,
          StatusId: resp.jsonData.testInstance?.statusId || null,
          ApplicantScore: getActiveScoreFromList(scores, Enums.TEST_SCORE_TYPES.Applicant, activeByKey),
          CompanyScore: getActiveScoreFromList(scores, Enums.TEST_SCORE_TYPES.Company, activeByKey),
          OverallScore: getActiveScoreFromList(scores, Enums.TEST_SCORE_TYPES.Overall, activeByKey),
        };
        
        setPayload(initialPayload);
        setOriginalPayload(JSON.parse(JSON.stringify(initialPayload))); // Deep copy for revert
        setHasChanges(testInstanceId === 0); // Mark as having changes for new records
      } else {
        const message = "Failed to load test instance details";
        setLoadError(message);
        toast.error(message);
      }
    } catch (error) {
      console.error("Error loading test instance:", error);
      const message = "Error loading test instance details";
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
      if (updateSpinner) updateSpinner(false);
    }
  }

  function handleClose() {
    setInstanceData(null);
    setPayload(null);
    setOriginalPayload(null);
    setHasChanges(false);
    setActiveTab("summary");
    setLocalDataPoints([]);
    setBaselineDataPoints([]);
    setEditingDataRow(null);
    setTabsContentHeightPx(null);
    setShowAllInstanceData(false);
    setLoadError(null);
    onClose();
  }

  const dataRowEditBusy = editingDataRow !== null;
  const canEditInstanceData =
    canEditTestInstance && testInstanceId !== 0 && !loading;

  function startCreateDataRow(e: React.MouseEvent) {
    e.stopPropagation();
    if (!canEditInstanceData || dataRowEditBusy) {
      return;
    }
    tempDataRowIdRef.current -= 1;
    const newRow: InstanceDataPointRow = {
      id: tempDataRowIdRef.current,
      dataKey: "",
      dataValue: "",
      active: true,
      createdTime: "",
    };
    setLocalDataPoints((prev) => [newRow, ...prev]);
    setEditingDataRow({ ...newRow });
  }

  function startDataEdit(e: React.MouseEvent, row: InstanceDataPointRow) {
    e.stopPropagation();
    if (!canEditInstanceData || !row.active) {
      return;
    }
    if (editingDataRow && editingDataRow.id <= 0) {
      setLocalDataPoints((prev) => prev.filter((r) => r.id !== editingDataRow.id));
    }
    setEditingDataRow({ ...row });
  }

  function cancelDataEdit(e: React.MouseEvent) {
    e.stopPropagation();
    if (editingDataRow != null && editingDataRow.id <= 0) {
      setLocalDataPoints((prev) => prev.filter((r) => r.id !== editingDataRow.id));
    }
    setEditingDataRow(null);
  }

  function onChangeDataField(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const field = e.target.getAttribute("data-field");
    if (!field || !editingDataRow) {
      return;
    }
    const value = e.target.value;
    setEditingDataRow((prev) => (prev ? { ...prev, [field]: value } : prev));
  }

  function commitDataRowEditLocally(e: React.MouseEvent) {
    e.stopPropagation();
    if (!editingDataRow || testInstanceId === 0) {
      return;
    }
    const key = editingDataRow.dataKey.trim();
    if (!key) {
      toast.error("Name is required.");
      return;
    }
    const merged: InstanceDataPointRow = {
      ...editingDataRow,
      dataKey: key,
      dataValue: editingDataRow.dataValue.trim(),
    };
    setLocalDataPoints((prev) =>
      prev.map((r) => (r.id === editingDataRow.id ? merged : r))
    );
    setEditingDataRow(null);
  }

  function removeDataRowLocally(rowId: number) {
    if (!canEditInstanceData || dataRowEditBusy || testInstanceId === 0) {
      return;
    }
    const isTemp = rowId <= 0;
    if (
      !isTemp &&
      !window.confirm("Remove this data row? It will be deleted when you save changes.")
    ) {
      return;
    }
    if (editingDataRow?.id === rowId) {
      setEditingDataRow(null);
    }
    setLocalDataPoints((prev) => prev.filter((r) => r.id !== rowId));
  }

  function toggleRowActiveLocal(rowId: number) {
    if (!canEditInstanceData || dataRowEditBusy || testInstanceId === 0) {
      return;
    }
    setLocalDataPoints((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, active: !r.active } : r))
    );
  }

  function handleSummaryScoreValueChange(field: string, rawValue: string) {
    if (!canEditInstanceData || testInstanceId === 0) {
      return;
    }
    updatePayloadField(field, rawValue);
  }

  async function syncInstanceDataToServer(): Promise<boolean> {
    if (!testId || testInstanceId <= 0) {
      return true;
    }

    const baseline = baselineDataPoints;
    const initialLocal = localDataPoints;

    if (instanceDataRowsEqual(initialLocal, baseline)) {
      return true;
    }

    const toastErr = (msg: string) => {
      toast.error(msg);
      return false;
    };

    const removedRows = baseline.filter(
      (b) => b.id > 0 && !initialLocal.some((l) => l.id === b.id)
    );

    let wl = initialLocal.map((r) => ({ ...r }));
    const rowIdReplacements = new Map<number, number>();

    function resolveId(id: number): number {
      let x = id;
      while (rowIdReplacements.has(x)) {
        x = rowIdReplacements.get(x)!;
      }
      return x;
    }

    function findWlRow(baselineId: number): InstanceDataPointRow | undefined {
      const curId = resolveId(baselineId);
      return wl.find((r) => r.id === curId);
    }

    for (const b of removedRows) {
      if (!b.active) {
        continue;
      }
      const resp = await RightTimeApi.deleteAsync(
        `/Test/${testId}/Instance/${testInstanceId}/Data/${b.id}`
      );
      if (!resp?.ok) {
        return toastErr(apiMessage(resp, "Failed to delete instance data row."));
      }
    }

    for (const b of baseline) {
      if (b.id <= 0 || !b.active) {
        continue;
      }
      const row = findWlRow(b.id);
      if (!row) {
        continue;
      }
      if (row.dataKey === b.dataKey && row.dataValue === b.dataValue) {
        continue;
      }
      const body = {
        dataKey: row.dataKey.trim(),
        dataValue: row.dataValue.trim() === "" ? null : row.dataValue.trim(),
      };
      const curId = resolveId(b.id);
      const resp = await RightTimeApi.putAsync(
        `/Test/${testId}/Instance/${testInstanceId}/Data/${curId}`,
        body
      );
      if (!resp?.ok) {
        return toastErr(apiMessage(resp, "Failed to update instance data row."));
      }
      const rawId =
        resp?.jsonData && typeof resp.jsonData === "object" && resp.jsonData !== null && "id" in resp.jsonData
          ? Number((resp.jsonData as { id?: unknown }).id)
          : NaN;
      if (Number.isFinite(rawId) && rawId !== curId) {
        rowIdReplacements.set(curId, rawId);
        wl = wl.map((r) => (r.id === curId ? { ...r, id: rawId } : r));
      }
    }

    for (const b of baseline) {
      if (b.id <= 0 || !b.active) {
        continue;
      }
      const row = findWlRow(b.id);
      if (!row || row.active) {
        continue;
      }
      const curId = resolveId(b.id);
      const resp = await RightTimeApi.patchAsync(`/Test/${testId}/Instance/${testInstanceId}/Data/${curId}`, {
        active: false,
      });
      if (!resp?.ok) {
        return toastErr("Failed to deactivate instance data row.");
      }
    }

    for (const b of baseline) {
      if (b.id <= 0 || b.active) {
        continue;
      }
      const row = findWlRow(b.id);
      if (!row || !row.active) {
        continue;
      }
      const curId = resolveId(b.id);
      const resp = await RightTimeApi.patchAsync(`/Test/${testId}/Instance/${testInstanceId}/Data/${curId}`, {
        active: true,
      });
      if (!resp?.ok) {
        return toastErr("Failed to activate instance data row.");
      }
    }

    for (const b of baseline) {
      if (b.id <= 0 || b.active) {
        continue;
      }
      const row = findWlRow(b.id);
      if (!row || !row.active) {
        continue;
      }
      if (row.dataKey === b.dataKey && row.dataValue === b.dataValue) {
        continue;
      }
      const body = {
        dataKey: row.dataKey.trim(),
        dataValue: row.dataValue.trim() === "" ? null : row.dataValue.trim(),
      };
      const curId = resolveId(b.id);
      const resp = await RightTimeApi.putAsync(
        `/Test/${testId}/Instance/${testInstanceId}/Data/${curId}`,
        body
      );
      if (!resp?.ok) {
        return toastErr(apiMessage(resp, "Failed to update instance data row."));
      }
      const rawId =
        resp?.jsonData && typeof resp.jsonData === "object" && resp.jsonData !== null && "id" in resp.jsonData
          ? Number((resp.jsonData as { id?: unknown }).id)
          : NaN;
      if (Number.isFinite(rawId) && rawId !== curId) {
        rowIdReplacements.set(curId, rawId);
        wl = wl.map((r) => (r.id === curId ? { ...r, id: rawId } : r));
      }
    }

    for (const row of wl) {
      if (row.id > 0) {
        continue;
      }
      if (!row.active) {
        continue;
      }
      const key = row.dataKey.trim();
      if (!key) {
        continue;
      }
      const body = {
        dataKey: key,
        dataValue: row.dataValue.trim() === "" ? null : row.dataValue.trim(),
      };
      const resp = await RightTimeApi.postAsync(`/Test/${testId}/Instance/${testInstanceId}/Data`, body);
      if (!resp?.ok) {
        return toastErr(apiMessage(resp, "Failed to create instance data row."));
      }
    }

    return true;
  }

  function handleCancel() {
    if (originalPayload) {
      setPayload(JSON.parse(JSON.stringify(originalPayload))); // Restore original values
      setHasChanges(false);
    }
    setLocalDataPoints(baselineDataPoints.map((r) => ({ ...r })));
    setEditingDataRow(null);
  }

  function updatePayloadField(field, value) {
    setPayload(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  }

  function handleSiteChange(event) {
    const siteId = event.target.value ? parseInt(event.target.value) : null;
    updatePayloadField('SiteId', siteId);
  }

  function handleJobChange(event) {
    const jobId = event.target.value ? parseInt(event.target.value) : null;
    updatePayloadField('JobId', jobId);
  }

  function handleCompanyTestTypeChange(event) {
    const companyTestTypeId = event.target.value ? parseInt(event.target.value) : null;
    updatePayloadField('CompanyTestTypeId', companyTestTypeId);
  }

  function handleStatusChange(event) {
    const statusId = event.target.value ? parseInt(event.target.value) : null;
    updatePayloadField('StatusId', statusId);
  }
  function handleStateChange(event) {
    const stateId = event.target.value ? parseInt(event.target.value) : null;
    updatePayloadField('StateId', stateId);
  }

  function handleDateChange(event) {
    updatePayloadField('Date', event?.target?.value || "");
  }

  async function handleSaveChanges() {
    if (!hasChanges && !instanceDataDirty && !scoresDirty) {
      return;
    }
    if (updateSpinner) updateSpinner(true);

    try {
      if (testInstanceId > 0 && instanceDataDirty) {
        const dataOk = await syncInstanceDataToServer();
        if (!dataOk) {
          return;
        }
      }

      if (hasChanges || scoresDirty) {
        console.log("Saving test instance with payload:", payload);
        const resp = await RightTimeApi.putAsync(`/Test/${testId}/Instance/${testInstanceId}`, payload);

        console.log("API response:", { ok: resp?.ok, status: resp?.status, jsonData: resp?.jsonData });

        if (resp && resp.ok) {
          toast.success("Test instance updated successfully");

          // Check if response contains download URLs - if so, close modal immediately before initiating downloads
          const downloadUrl = resp?.jsonData?.downloadUrl;
          const downloadUrls = resp?.jsonData?.downloadUrls;

          if (downloadUrl || downloadUrls) {
            console.log("Downloads detected in response - closing modal before download initiation");
            if (updateSpinner) updateSpinner(false);
            handleClose();

            // Trigger downloads after modal close is queued (stay on page; no window.open)
            setTimeout(() => {
              void (async () => {
                const urls = [
                  ...(downloadUrl ? [downloadUrl] : []),
                  ...(Array.isArray(downloadUrls) ? downloadUrls : []),
                ].filter(Boolean);

                for (const url of urls) {
                  const ok = await downloadAuthenticatedFile(url, "download");
                  if (!ok) toast.error("Failed to download file.");
                }
              })();
            }, 100);
          } else {
            // Standard flow: close modal after API response (no downloads)
            console.log("Calling handleClose to close modal");
            handleClose();
          }
        } else {
          console.warn("API response not ok:", resp?.status, resp?.statusText, resp?.jsonData);
          toast.error(apiMessage(resp, "Failed to update test instance"));
        }
      } else {
        toast.success("Instance data saved.");
        handleClose();
      }
    } catch (error) {
      console.error("Error updating test instance:", error);
      toast.error("Error updating test instance");
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  async function handleResetScores() {
    if (!testId) return;

    if (updateSpinner) updateSpinner(true);
    try {
      const resp = await RightTimeApi.putAsync(`/Test/${testId}/ResetScores`, {});

      if (resp && resp.ok) {
        toast.success("Scores reset successfully");
        await loadTestInstance();
      } else {
        toast.error("Failed to reset scores");
      }
    } catch (error) {
      console.error("Error resetting scores:", error);
      toast.error("Error resetting scores");
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  if (!isOpen) {
    return null;
  }

  const listedInstanceDataRows = showAllInstanceData
    ? localDataPoints
    : localDataPoints.filter((r) => r.active);
  const activeInstanceDataRowCount = localDataPoints.filter((r) => r.active).length;

  const instanceLoaded = Boolean(instanceData?.testInstance);
  const cancelFooter = (
    <div className="footerRight">
      <button className="btn btn-secondary" onClick={handleClose}>
        Cancel
      </button>
    </div>
  );
  const footer = instanceLoaded ? (
    <>
      <div className="footerLeft">
        <button className="btn btn-warning" onClick={handleResetScores}>
          Reset
        </button>
      </div>
      <div className="footerRight">
        <button className="btn btn-secondary" onClick={handleClose}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSaveChanges}
          disabled={!hasChanges && !instanceDataDirty && !scoresDirty}
        >
          Save Changes
        </button>
      </div>
    </>
  ) : (
    cancelFooter
  );

  const modalTitleId =
    instanceData?.testInstance && testInstanceId !== 0
      ? instanceData.testInstance.id
      : testInstanceId != null && testInstanceId !== 0
        ? testInstanceId
        : null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        className="testInstanceModal"
        title={`Test Instance Details ${modalTitleId != null ? `(${modalTitleId})` : testInstanceId === 0 ? '(New)' : ''}`}
        footer={footer}
        bodyClassName={instanceLoaded ? "withTabs" : ""}
      >
          {loadError ? (
            <div className="loadingMessage">{loadError}</div>
          ) : loading ? (
            <div className="loadingMessage">Loading test instance details...</div>
          ) : instanceData?.testInstance ? (
            <>
              <div className="modalTabsContainer">
                <div className="tab-container">
                  <button
                    type="button"
                    className={`modal-tab ${activeTab === "summary" ? "active" : ""}`}
                    onClick={() => setActiveTab("summary")}
                  >
                    Summary
                  </button>
                  <button
                    type="button"
                    className={`modal-tab ${activeTab === "data" ? "active" : ""}`}
                    onClick={() => setActiveTab("data")}
                  >
                    Data{" "}
                    {activeInstanceDataRowCount > 0 ? (
                      <span className="badge">{activeInstanceDataRowCount}</span>
                    ) : null}
                  </button>
                </div>
              </div>

              <div className="tabsContentContainer" style={tabsContentContainerStyle}>
              <div
                ref={summaryPanelRef}
                className={`tab summary ${activeTab === "summary" ? "active" : ""}`}
              >
              {/* Instance Summary - Matching Test Scores Table Layout */}
              <div className="tableWrapper factTable formTable">
                <table>
                  <caption>
                    <span className="title">Test Instance Summary</span>
                  </caption>
                  <tbody>
                    <tr>
                      <th>Date</th>
                      <td>
                        <DateTimePicker
                          field="Date"
                          pickerTitle="Date"
                          value={payload?.Date || ""}
                          onChange={handleDateChange}
                          displayAsText
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Company Test Type</th>
                      <td>
                        {isSystemAdmin && instanceData.availableCompanyTestTypes && instanceData.availableCompanyTestTypes.length > 0 ? (
                          <select
                            value={payload?.CompanyTestTypeId || ""}
                            onChange={handleCompanyTestTypeChange}
                            className="editBoxes"
                            style={{ width: '100%', padding: '4px' }}
                          >
                            <option value="">-- Select Type --</option>
                            {instanceData.availableCompanyTestTypes.map(ctt => (
                              <option key={ctt.id} value={ctt.id}>
                                {ctt.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          instanceData.testInstance.companyTestType?.name || "N/A"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>Job</th>
                      <td>
                        {canEditTestInstance && instanceData.availableJobs && instanceData.availableJobs.length > 0 ? (
                          <select
                            value={payload?.JobId || ""}
                            onChange={handleJobChange}
                            className="editBoxes"

                            style={{ width: '100%', padding: '4px' }}
                          >
                            <option value="">-- Select Job --</option>
                            {instanceData.availableJobs.map(job => (
                              <option key={job.id} value={job.id}>
                                {job.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          instanceData.testInstance.companyJobName || "N/A"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>Site</th>
                      <td>
                        {canEditTestInstance && instanceData.availableSites && instanceData.availableSites.length > 0 ? (
                          <select
                            value={payload?.SiteId || ""}
                            onChange={handleSiteChange}
                            className="editBoxes"

                            style={{ width: '100%', padding: '4px' }}
                          >
                            <option value="">-- Select Site --</option>
                            {instanceData.availableSites.map(site => (
                              <option key={site.id} value={site.id}>
                                {site.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          instanceData.testInstance.siteName || "N/A"
                        )}
                      </td>
                    </tr>

                    <tr>
                      <th>Supervisor</th>
                      <td>
                        {instanceData.testInstance.supervisor ? (
                          <a 
                            href="#" 
                            onClick={(e) => {
                              e.preventDefault();
                              setSupervisorSelectInstanceId(instanceData.testInstance.id);
                            }}
                            style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                          >
                            {instanceData.testInstance.supervisor}
                          </a>
                        ) : (
                          <a 
                            href="#" 
                            onClick={(e) => {
                              e.preventDefault();
                              setSupervisorSelectInstanceId(instanceData.testInstance.id);
                            }}
                            style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                          >
                            Select
                          </a>
                        )}
                      </td>
                    </tr>

                    <tr>
                      <th>Test Instance Status</th>
                      <td>
                        <select
                          value={payload?.StatusId || ""}
                          onChange={handleStatusChange}
                          style={{ width: '100%', padding: '4px' }}
                          disabled={!instanceData.testInstanceStatuses || instanceData.testInstanceStatuses.length === 0 || !isSystemAdmin}
                          className="editBoxes"
                        >
                          <option value=""></option>
                          {instanceData.testInstanceStatuses && instanceData.testInstanceStatuses.map(status => (
                            <option
                              key={status.id}
                              value={status.id}
                            >
                              {status.name}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>

                    <tr>
                      <th>Status</th>
                      <td>
                        <select
                          value={payload?.companyTestStatusId || ""}
                          onChange={handleStatusChange}
                          style={{ width: '100%', padding: '4px' }}
                          className={`editBoxes ${payload?.companyTestStatusId && instanceData.testInstance.companyTestType.statuses?.find(s => s.ctsId === payload.companyTestStatusId)?.billable ? 'billable' : ''}`}
                          disabled={!instanceData?.testInstance?.companyTestType?.statuses || instanceData.testInstance.companyTestType.statuses.length === 0 || !isSystemAdmin}
                        >
                          <option value=""></option>
                          {instanceData.testInstance.companyTestType.statuses && instanceData.testInstance.companyTestType.statuses.map(status => (
                            <option 
                              key={status.ctsId} 
                              value={status.ctsId}
                              className={status.billable ? "billable" : ""}
                            >
                              {status.display}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                    {SUMMARY_SCORE_FIELDS.map(({ field, label, scoreType }) => {
                      const editable = canEditInstanceData && testInstanceId > 0;
                      const readOnlyValue = getActiveScoreFromList(
                        instanceData.testInstance?.scores,
                        scoreType,
                        instanceData.testInstance?.instanceDataActiveByKey
                      );
                      const hasReadOnlyValue = readOnlyValue.trim() !== "";

                      if (!editable) {
                        if (!hasReadOnlyValue) {
                          return null;
                        }
                        return (
                          <tr key={`summary-score-${field}`}>
                            <th>{label}</th>
                            <td>{readOnlyValue}</td>
                          </tr>
                        );
                      }

                      const value = payload?.[field] ?? "";
                      return (
                        <tr key={`summary-score-${field}`}>
                          <th>{label}</th>
                          <td>
                            <input
                              type="text"
                              className="editBoxes"
                              style={{ width: "100%", padding: "4px", boxSizing: "border-box" }}
                              value={value}
                              onChange={(e) =>
                                handleSummaryScoreValueChange(field, e.target.value)
                              }
                              aria-label={label}
                            />
                          </td>
                        </tr>
                      );
                    })}
                    <tr>
                      <th>Fee Item Type</th>
                      <td>
                        {instanceData.testInstance.invoiceLineItems && instanceData.testInstance.invoiceLineItems.length > 0
                          ? (
                              <>
                                {instanceData.testInstance.invoiceLineItems[0].companyFeeItemName || ""}
                                {instanceData.testInstance.invoiceLineItems[0].amount != null && 
                                  ` ($${instanceData.testInstance.invoiceLineItems[0].amount.toFixed(2)})`
                                }
                              </>
                            )
                          : ""}
                      </td>
                    </tr>
                    <tr>
                      <th>Instance ID</th>
                      <td>{instanceData.testInstance.id}</td>
                    </tr>
                    <tr>
                      <th>Test ID</th>
                      <td>{instanceData.testInstance.testId}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Additional Details */}
              {instanceData.testInstance.additionalData && Object.keys(instanceData.testInstance.additionalData).length > 0 && (
                <div className="tableWrapper factTable">
                  <table>
                    <caption>
                      <span className="title">Additional Information</span>
                    </caption>
                    <tbody>
                      {Object.entries(instanceData.testInstance.additionalData).map(([key, value]) => (
                        <tr key={key}>
                          <td>{key}</td>
                          <td>{value !== null && value !== undefined ? String(value) : "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </div>

              <div className={`tab data ${activeTab === "data" ? "active" : ""}`}>
                <div className="tableWrapper dimensionTable scrollable companyRolesTable companiesTable instanceDataTable">
                  <table>
                    <caption>
                      <span className="title">Test instance data</span>
                      {canEditInstanceData ? (
                        <span className="captionRight">
                          <FaPlusSquare
                            className={`actionIcon plusIcon${dataRowEditBusy ? " disabled" : ""}`}
                            onClick={startCreateDataRow}
                            title={dataRowEditBusy ? "Finish or cancel the current edit first" : "Add data row"}
                          />
                        </span>
                      ) : null}
                    </caption>
                    <thead>
                      <tr>
                        <th
                          className="toggleColumnHeader clickable"
                          onClick={() => setShowAllInstanceData(!showAllInstanceData)}
                        >
                          {showAllInstanceData ? "All" : "Active"}
                        </th>
                        <th>Name</th>
                        <th>Value</th>
                        <th>Created</th>
                        {canEditInstanceData ? <th className="actions right" /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {listedInstanceDataRows.length === 0 && !editingDataRow ? (
                        <tr>
                          <td colSpan={canEditInstanceData ? 5 : 4} className="centerTextCell">
                            {testInstanceId === 0
                              ? "Save this test instance before adding data rows."
                              : localDataPoints.length === 0
                                ? "No data rows yet. Use + to add a row or upload a grading Excel file from Test Details → Instances."
                                : "No active rows. Click the Active column header to show all rows (including inactive history)."}
                          </td>
                        </tr>
                      ) : (
                        listedInstanceDataRows.map((row) => {
                          const isThisRowEditing =
                            editingDataRow !== null && editingDataRow.id === row.id;
                          const showEditChrome = canEditInstanceData;
                          const editDisabled =
                            (dataRowEditBusy && !isThisRowEditing) || !row.active;
                          return (
                            <tr
                              key={row.id <= 0 ? `new-data-row-${row.id}` : `d-${row.id}`}
                              className={row.active ? undefined : "instanceDataInactive"}
                            >
                              <td
                                className="centerAlignedCell clickable"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!canEditInstanceData || dataRowEditBusy) {
                                    return;
                                  }
                                  toggleRowActiveLocal(row.id);
                                }}
                                title={
                                  canEditInstanceData && !dataRowEditBusy
                                    ? "Click to toggle active; save with Save Changes"
                                    : undefined
                                }
                              >
                                {row.active ? (
                                  <MdCheckBox className="actionIcon" />
                                ) : (
                                  <MdCheckBoxOutlineBlank className="actionIcon" />
                                )}
                              </td>
                              <td>
                                {isThisRowEditing ? (
                                  <input
                                    value={editingDataRow?.dataKey ?? ""}
                                    data-field="dataKey"
                                    onChange={onChangeDataField}
                                    className="inlineEdit nameEdit"
                                    autoFocus
                                    aria-label="Data name"
                                  />
                                ) : (
                                  row.dataKey || "—"
                                )}
                              </td>
                              <td className="instanceDataValueCell">
                                {isThisRowEditing ? (
                                  <textarea
                                    value={editingDataRow?.dataValue ?? ""}
                                    data-field="dataValue"
                                    onChange={onChangeDataField}
                                    className="inlineEdit instanceDataValueEdit"
                                    rows={3}
                                    aria-label="Data value"
                                  />
                                ) : row.dataValue !== "" ? (
                                  row.dataValue
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td>
                                {row.createdTime
                                  ? Helpers.formatFullDateTime(row.createdTime)
                                  : "—"}
                              </td>
                              {showEditChrome ? (
                                <td className="actions right">
                                  {isThisRowEditing ? (
                                    <>
                                      <RiSave3Fill
                                        className="save actionIcon"
                                        onClick={commitDataRowEditLocally}
                                        title="Apply (save to server with Save Changes)"
                                      />
                                      <ImCancelCircle
                                        className="cancel actionIcon"
                                        onClick={cancelDataEdit}
                                        title="Cancel"
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <CiEdit
                                        className={`actionIcon editIcon${editDisabled ? " disabled" : ""}`}
                                        onClick={(ev) => {
                                          if (!editDisabled) {
                                            startDataEdit(ev, row);
                                          }
                                        }}
                                        title={
                                          !row.active
                                            ? "Inactive rows cannot be edited"
                                            : editDisabled
                                              ? "Finish the current edit first"
                                              : "Edit"
                                        }
                                      />
                                      <IoTrashOutline
                                        className={`actionIcon deleteIcon${
                                          dataRowEditBusy || (row.id > 0 && !row.active) ? " disabled" : ""
                                        }`}
                                        onClick={() => {
                                          if (dataRowEditBusy) {
                                            return;
                                          }
                                          if (row.id > 0 && !row.active) {
                                            return;
                                          }
                                          removeDataRowLocally(row.id);
                                        }}
                                        title={
                                          row.id > 0 && !row.active
                                            ? "Inactive rows cannot be removed"
                                            : dataRowEditBusy
                                              ? "Finish the current edit first"
                                              : "Remove row (saved when you click Save Changes)"
                                        }
                                      />
                                    </>
                                  )}
                                </td>
                              ) : null}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              </div>
            </>
          ) : (
            <div className="noData">No test instance data available.</div>
          )}
      </Modal>

      <SupervisorSelectModal
        isOpen={supervisorSelectInstanceId !== null}
        onClose={() => { setSupervisorSelectInstanceId(null); loadTestInstance()}}
        companyId={instanceData?.testInstance?.companyTestType?.companyId}
        testInstanceId={supervisorSelectInstanceId}
        supervisorsInstanceId={instanceData?.testInstance?.supervisorTestInstanceId}
      />
    </>
  );
}


